import React, { Fragment, useEffect, useRef, useState } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { Alert, Grid } from "@mui/material";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import moment from "moment";
import { ArrowBack, CloudUpload, DomainAdd } from "@mui/icons-material";
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db, auth } from "../../firebase";
import { uploadImage } from "../../utils/awsUtils";
import { useUser } from "../context/UserContext";

function convertToSlug(text) {
  return text.toLowerCase().replace(/ /g, "-").replace(/&/g, "and").replace(/[^\w-]+/g, "");
}

const CreateEvent = () => {
  const [title, setTitle] = useState("");
  const [venue, setVenue] = useState("");
  const [date, setDate] = useState("");
  const [dateView, setDateView] = useState("");
  const [CKData, setCKData] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const fileRef = useRef();
  const navigate = useNavigate();
  const { userRole, loading: roleLoading } = useUser();

  useEffect(() => {
    if (!roleLoading && userRole === "operator") {
      navigate("/home");
    }
  }, [roleLoading, userRole, navigate]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setErrorMsg("Image must be under 2MB");
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setErrorMsg("");
  };

  const handleEvent = async () => {
    if (!title) return setErrorMsg("Title cannot be empty");
    if (!imageFile) return setErrorMsg("Please select an image");
    if (!venue) return setErrorMsg("Venue cannot be empty");
    if (!date) return setErrorMsg("Date cannot be empty");
    if (!CKData) return setErrorMsg("Body cannot be empty");

    setLoading(true);
    try {
      const { imgUrl, imgId } = await uploadImage(imageFile);

      let slug = convertToSlug(title);
      const existing = await getDocs(query(collection(db, "events"), where("slug", "==", slug)));
      if (!existing.empty) slug = slug + "-" + Math.random().toString(36).substring(5);

      await addDoc(collection(db, "events"), {
        title,
        imgSrc: imgUrl,
        imgId,
        body: CKData,
        slug,
        venue,
        eventdate: date,
        createdAt: serverTimestamp(),
      });

      const userName = auth.currentUser?.email || "Admin";
      await addDoc(collection(db, "notifications"), {
        name: userName,
        message: `Created Event: ${title}`,
        createdAt: serverTimestamp(),
      });

      toast("Event created successfully!", { type: "success" });
      setTimeout(() => navigate("/event"), 2000);
    } catch (err) {
      setErrorMsg(err.message || "Failed to create event");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <div className="min-h-screen bg-[#f5f7fe] p-3 sm:p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate("/event")}
              className="p-2 rounded-xl hover:bg-white border border-transparent hover:border-gray-100 transition"
            >
              <ArrowBack className="text-gray-400" style={{ fontSize: 20 }} />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#000C66] font-aref">Create Event</h1>
              <p className="text-gray-400 text-xs font-abel mt-0.5">Add a new church event</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-8">
            {errorMsg && <Alert severity="error" className="mb-5" onClose={() => setErrorMsg("")}>{errorMsg}</Alert>}

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 font-abel">Event Title *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => { setTitle(e.target.value); setErrorMsg(""); }}
                  placeholder="Event title"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-abel text-sm text-gray-700 focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 font-abel">Date & Time *</label>
                <input
                  type="datetime-local"
                  value={dateView}
                  onChange={(e) => {
                    setDate(moment(e.target.value).toDate());
                    setDateView(e.target.value);
                    setErrorMsg("");
                  }}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-abel text-sm text-gray-700 focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 font-abel">Venue *</label>
                <input
                  type="text"
                  value={venue}
                  onChange={(e) => { setVenue(e.target.value); setErrorMsg(""); }}
                  placeholder="Event venue / location"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-abel text-sm text-gray-700 focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 font-abel">
                  Event Image * <span className="text-gray-300 normal-case">(max 2MB)</span>
                </label>
                {imagePreview && (
                  <img src={imagePreview} alt="preview" className="h-24 w-24 object-cover rounded-xl border border-gray-100 mb-2" />
                )}
                <div
                  className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:border-[#000C66] transition"
                  onClick={() => fileRef.current.click()}
                >
                  <CloudUpload className="text-gray-300" style={{ fontSize: 24 }} />
                  <p className="text-xs text-gray-400 font-abel mt-1">
                    {imageFile ? imageFile.name : "Click to select image"}
                  </p>
                </div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
              </Grid>

              <Grid item xs={12}>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 font-abel">Event Description *</label>
                <CKEditor
                  editor={ClassicEditor}
                  data={CKData}
                  onChange={(_, editor) => { setCKData(editor.getData()); setErrorMsg(""); }}
                  onReady={(editor) => {
                    editor.editing.view.change((writer) => {
                      writer.setStyle("height", "220px", editor.editing.view.document.getRoot());
                    });
                  }}
                />
              </Grid>

              <Grid item xs={12} className="flex justify-end">
                <button
                  onClick={handleEvent}
                  disabled={loading}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-[#000C66] text-white px-8 py-3 rounded-xl font-abel font-semibold hover:bg-blue-900 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <DomainAdd style={{ fontSize: 18 }} />
                  )}
                  {loading ? "Creating..." : "Create Event"}
                </button>
              </Grid>
            </Grid>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default CreateEvent;
